import { useId, useState } from 'react'
import { Button, Card, TableScroll } from '../../ui'
import {
  getFindings, getOverview, getReportSummary, getReportVersion, getWorkspace, referenceOf,
} from '../../lib/api'
import type { FindingSummary } from '../../lib/api'
import { agrees, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import { describeFailure, useApi } from './useApi'
import { formatDate } from './LiveFindings'
import { notLicensedAreaCount, notLicensedReportSentence } from './notLicensed'
import { checkName, gapDescriptions, partlyReadAreas, partlyReadPhrase, unconfirmedAreas } from './partlyRead'
import { ExecutiveSummary } from './ExecutiveSummary'
import type { SummaryLoad } from './ExecutiveSummary'
import './live.css'

/** The order a report reads in. Worst first, and every level named even when it is empty. */
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'] as const

/**
 * The server's maximum page. A report that silently stopped at the cap would be a document
 * somebody hands to an auditor with findings missing and no indication of it, so this number is
 * compared against what came back and said out loud when it bites.
 */
const MAX_ROWS = 100

/**
 * The assessment, as a document.
 *
 * THIS PAGE IS LIVE, and it says so. It composes the same reads the rest of the app uses into
 * something printable, so it always reflects the current posture — later fixes and re-triage
 * included. The heading carries the date of the SCAN rather than the date of the download, because
 * a document titled with today's date built from a three-week-old assessment is the specific lie
 * this product is built to avoid. It also states the versions the scan ran under and discloses if
 * the catalogue or a baseline has since moved (/api/reports/version).
 *
 * THE STORED ARTIFACT IS SEPARATE. A terminal scan seals an immutable, encrypted, tamper-evident
 * copy of the matrix it produced (migration 0143); that is the reproducible "report as the scan
 * recorded it", which this live page cannot reconstruct once findings move. It is offered here as a
 * download (/api/reports/findings-sealed.csv) alongside the live CSV, clearly distinguished — the
 * live one is the current picture, the sealed one is frozen and carries an integrity reference.
 *
 * COLOUR CARRIES SEVERITY AND NOTHING ELSE. The tokens it uses ship with their measured contrast
 * pairs, so a Critical chip is white on #8c0f2e at 9.45:1 rather than a colour picked to look
 * serious. Nothing here is coloured decoratively: a reader who cannot tell red from amber still
 * has the level written next to every bar and in every row.
 *
 * SCOPE IS PART OF THE DOCUMENT. A report filtered to one area is a different claim from a
 * report of the whole tenant, and the two must never be mistakable once printed — so the scope
 * is stated in the header, in the summary sentence, and again above the findings table, and the
 * selector that sets it is the one control the print stylesheet keeps.
 */
/** The LIVE findings matrix — the current posture. */
const LIVE_CSV = '/api/reports/findings.csv'

/**
 * The SEALED record — the matrix as the latest terminal scan recorded it, immutable and integrity-
 * verified on the server. Offered only when one exists; the server returns 404 otherwise.
 */
const SEALED_CSV = '/api/reports/findings-sealed.csv'

/**
 * Downloads a findings CSV and says why when it cannot.
 *
 * FETCHED, not clicked. A plain link to the export failed silently: an expired session, a missing
 * sealed record, or a sealed record that failed its integrity check all ended in a browser download
 * error (or nothing) with no word on this page. Fetching lets the page name the reason. The file is
 * buffered (the live export is capped at 5000 rows) and saved under the server's own filename.
 * Returns null on success, or the sentence to show.
 */
async function downloadCsv(href: string, sealed: boolean): Promise<string | null> {
  let response: Response
  try {
    response = await fetch(href, { credentials: 'same-origin' })
  } catch {
    return describeFailure(0)
  }

  if (!response.ok) {
    if (response.status === 401) return describeFailure(401)
    if (sealed && response.status === 404) return 'No sealed record exists yet for this workspace.'
    if (sealed && response.status === 409) {
      return 'The sealed record failed its integrity check, so it was not downloaded. Please tell us.'
    }
    return 'The download could not be prepared. Nothing was saved.'
  }

  // Inside its own catch: the body streams after the headers, so a connection dropped mid-file
  // rejects HERE -- outside the fetch's catch -- and used to leave both buttons on "Preparing…".
  let blob: Blob
  try {
    blob = await response.blob()
  } catch {
    return describeFailure(0)
  }
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const filename = named ? decodeURIComponent(named[1]!) : 'defenssive-findings.csv'

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return null
}

export function LiveReports() {
  const session = useSession()

  // null is the whole tenant. An area name is one section of it.
  const [area, setArea] = useState<string | null>(null)

  // Which download is being prepared, and why the last one failed (if it did).
  const [preparing, setPreparing] = useState<'live' | 'sealed' | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  async function download(kind: 'live' | 'sealed') {
    if (preparing) return
    setPreparing(kind)
    setDownloadError(null)
    // Whatever happens, the buttons come back: an unexpected throw must not strand them disabled.
    try {
      setDownloadError(await downloadCsv(kind === 'live' ? LIVE_CSV : SEALED_CSV, kind === 'sealed'))
    } catch {
      setDownloadError(describeFailure(0))
    } finally {
      setPreparing(null)
    }
  }

  const workspace = useApi(() => getWorkspace(), [])
  const overview = useApi(() => getOverview(), [])

  // Supplementary and best-effort: the report renders without it. It carries the versions the scan
  // ran under, live drift, and whether a sealed copy exists — used to replace a hard-coded baseline
  // line with the real provenance, and to offer the sealed download.
  const version = useApi(() => getReportVersion(), [])

  // The executive first page's actions and changes. Supplementary like the version read: the report
  // renders without it, and the page says so where it would have been.
  const summary = useApi(() => getReportSummary(), [])

  // Worst first, so if the cap is ever reached it is the least severe rows that are missing
  // rather than an arbitrary slice. Filtering happens on the SERVER -- filtering a capped page
  // in the browser would silently drop rows past the cap that belong to the chosen area.
  const findings = useApi(
    () => getFindings({
      status: 'active',
      sort: 'severity',
      pageSize: MAX_ROWS,
      area: area ?? undefined,
    }),
    [area],
  )

  if (workspace.state === 'loading' || overview.state === 'loading') {
    return <div className="live-empty">Loading…</div>
  }

  if (workspace.state === 'error') {
    return <div className="live-error">{describeFailure(workspace.status)}</div>
  }

  if (overview.state === 'error') {
    return <div className="live-error">{describeFailure(overview.status)}</div>
  }

  if (
    !agrees(session, workspace.data.workspaceId) ||
    !agrees(session, overview.data.workspaceId)
  ) {
    return (
      <div className="live-error">
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </div>
    )
  }

  const w = workspace.data
  const { freshness } = overview.data

  // Areas complete only because nothing in them is licensed (0158), and the sentence naming what
  // was not assessed for want of a licence -- the printed twin of the CSV preamble's.
  const notLicensedCount = notLicensedAreaCount(freshness)
  const notLicensedLine = notLicensedReportSentence(freshness)

  // What qualifies a complete assessment, in the dashboard's words: the checks that saw only part of
  // what they needed ("1 check in Endpoint saw only part of what it needed."), NOT "N of them read
  // only part of what Microsoft holds", which the dashboard dropped as untrue -- a check can read
  // every device and fail to judge some. Its own sentence, so it opens with a capital.
  const partlyRead = partlyReadAreas(freshness)
  const partlyReadPhraseText = partlyReadPhrase(partlyRead)
  const partlyReadLine = partlyReadPhraseText
    ? `${partlyReadPhraseText.charAt(0).toUpperCase()}${partlyReadPhraseText.slice(1)}.`
    : null

  // Areas whose record of that could not be read back. The complete line is not left to stand
  // alone over them: nothing here can say they were read in full.
  const unconfirmed = unconfirmedAreas(freshness).length
  const unconfirmedLine = unconfirmed === 0
    ? null
    : `For ${unconfirmed === 1 ? '1 area' : `${unconfirmed} areas`}, this report could not confirm that `
      + 'every check read its evidence in full.'

  // Only trust the version read if it answered for the SAME workspace as the chrome — the same guard
  // the mandatory reads above make. If it is still loading, errored, or answered for another tenant,
  // versionData is null and the page falls back to the plain baseline line rather than showing stale
  // or mismatched provenance.
  const versionData =
    version.state === 'ready' && agrees(session, version.data.workspaceId) ? version.data : null

  // The version read is supplementary, but "must state its version" means a FAILURE to load it must be
  // visible — not silently rendered as a plain, version-less baseline line that reads as fine. Errored,
  // or answered for a different workspace, is "unavailable"; still loading is neither (no claim yet).
  const versionUnavailable =
    version.state === 'error' ||
    (version.state === 'ready' && !agrees(session, version.data.workspaceId))

  // The same guard for the executive page: an answer for another workspace is never printed under
  // this one's name, and reads as unavailable rather than as an empty list of actions.
  const summaryLoad: SummaryLoad =
    summary.state === 'loading'
      ? { state: 'loading' }
      : summary.state === 'ready' && agrees(session, summary.data.workspaceId)
        ? { state: 'ready', data: summary.data }
        : { state: 'unavailable' }

  const areas = Object.entries(overview.data.activeByArea).sort((a, b) => b[1] - a[1])
  const rows = findings.state === 'ready' ? findings.data.items : []
  const capped = rows.length >= MAX_ROWS

  // The server's own tally for the whole tenant, and for one area. Both exact: they are counted
  // across every finding rather than over the page that happens to be on screen.
  const wholeTenant = Object.values(overview.data.activeBySeverity).reduce((s, n) => s + n, 0)
  const scopeTotal = area === null ? wholeTenant : (overview.data.activeByArea[area] ?? 0)

  /*
    WHERE THE SEVERITY NUMBERS COME FROM, which is different for the two scopes and matters.

    For the whole tenant the server has already counted every finding by severity, so those are
    used as they are. For one area there is no such tally -- the overview counts areas and
    severities separately, never crossed -- so the breakdown is derived from the rows on screen.
    That is exact only while every one of that area's findings is on screen, which is why it is
    checked against the area's own total rather than assumed.
  */
  // A server that splits each area by severity (added for the dashboard) gives the exact breakdown
  // for one area too, and the rows on screen are then only the list. An older server does not, and
  // the breakdown is derived from the rows as before.
  const serverSplit = area !== null ? overview.data.activeByAreaAndSeverity?.[area] ?? null : null
  const derived = area !== null && serverSplit === null
  const fromRows: Record<string, number> = {}
  for (const row of rows) fromRows[row.severity] = (fromRows[row.severity] ?? 0) + 1

  const counts = derived ? fromRows : serverSplit ?? overview.data.activeBySeverity
  // Derived from rows only once the rows HAVE loaded: before that (or after a failed load) the
  // per-area breakdown read all zeros under a non-zero total, with a warning blaming page size.
  const breakdownReady = !derived || findings.state === 'ready'
  const countsAreExact = !derived || (findings.state === 'ready' && rows.length === scopeTotal)

  return (
    <>
      <div className="report-actions">
        <div className="report-scope-picker">
          <span className="report-scope-label">Report on</span>

          <button
            type="button"
            className="report-scope"
            aria-pressed={area === null}
            onClick={() => setArea(null)}
          >
            Everything
          </button>

          {areas.map(([name, count]) => (
            <button
              key={name}
              type="button"
              className="report-scope"
              aria-pressed={area === name}
              onClick={() => setArea(name)}
            >
              {name} <span className="report-scope-count">{count}</span>
            </button>
          ))}
        </div>

        {/*
          WHAT EACH OUTPUT IS FOR, AND WHAT IT HOLDS, BEFORE THE CLICK (owner approved, 2026-10-11).
          Three named outputs replace the buttons and the two paragraphs under them: export guidance
          is to label each format by its purpose and state its scope in numbers. The printable report
          follows the scope chosen above; the CSV is always the whole tenant; the sealed record is
          the scan it was sealed from.
        */}
        <div className="report-outputs">
          <section className="report-output" aria-labelledby="report-output-print">
            <h2 id="report-output-print" className="report-output-name">
              Printable report <span className="report-output-format">PDF</span>
            </h2>
            <p className="report-output-what">
              The report below, laid out to print: an executive summary on page one, then the detail.
            </p>
            <p className="report-output-scope">
              {freshness.status === null
                ? 'No assessment yet'
                : `${area ?? 'Everything'} · ${scopeTotal} open` +
                  (scopeTotal > MAX_ROWS ? `, the ${MAX_ROWS} most severe listed` : scopeTotal > 0 ? ', all listed' : '')}
            </p>
            <Button variant="primary" onClick={() => window.print()}>Print or save as PDF</Button>
          </section>

          <section className="report-output" aria-labelledby="report-output-csv">
            <h2 id="report-output-csv" className="report-output-name">
              All findings <span className="report-output-format">CSV</span>
            </h2>
            <p className="report-output-what">
              For spreadsheets: every open and set-aside finding with its source and framework
              mappings. Evidence stays on each finding.
            </p>
            <p className="report-output-scope">Whole tenant, current picture</p>
            <Button variant="primary" onClick={() => void download('live')} disabled={preparing !== null}>
              {preparing === 'live' ? 'Preparing…' : 'Download all findings (CSV)'}
            </Button>
          </section>

          {versionData?.sealed ? (
            <section className="report-output" aria-labelledby="report-output-sealed">
              <h2 id="report-output-sealed" className="report-output-name">
                Sealed record <span className="report-output-format">CSV</span>
              </h2>
              <p className="report-output-what">
                For auditors: the matrix frozen as the scan recorded it, with an integrity reference.
                It does not change when findings do.
              </p>
              <p className="report-output-scope">Recorded {formatDate(versionData.sealed.sealedAt)}</p>
              <Button variant="primary" onClick={() => void download('sealed')} disabled={preparing !== null}>
                {preparing === 'sealed' ? 'Preparing…' : 'Download sealed record (CSV)'}
              </Button>
            </section>
          ) : null}
        </div>

        {downloadError ? (
          <p className="live-error" role="alert" style={{ marginTop: 'var(--space-2)' }}>
            {downloadError}
          </p>
        ) : null}

        {/* The executive page is about the whole tenant, so it is not printed over a report on one
            area -- where it would sit above findings it does not describe. Said here, beside the
            control that decides it, rather than left for somebody to notice a missing page. */}
        {area !== null ? (
          <p className="report-scope-note">
            <strong>This is a partial report.</strong>{' '}
            <span>The executive summary covers the whole tenant, so it is printed only when the report is on Everything.</span>
          </p>
        ) : null}
      </div>

      {/* The report as a page on a desk, at the width it prints, with an outline beside it to move
          between its parts. On paper the desk, the shadow and the outline are gone. */}
      <div className="report-desk">
      <ReportOutline
        parts={[
          ...(area === null ? [{ id: 'report-exec', label: 'Executive summary' }] : []),
          { id: 'report-details', label: 'Assessment details' },
          { id: 'report-summary', label: 'Summary' },
          {
            id: 'report-findings',
            label: 'Findings',
            note: findings.state === 'ready' && capped ? `${rows.length} of ${scopeTotal}` : undefined,
          },
        ]}
      />
      <div className="report report-sheet">
        {area === null ? (
          <div id="report-exec" className="report-part">
          <ExecutiveSummary
            organisation={w.name?.trim() ? w.name : 'this workspace'}
            freshness={freshness}
            activeBySeverity={overview.data.activeBySeverity}
            suppressed={overview.data.suppressed}
            unreadControls={overview.data.unreadControls ?? []}
            summary={summaryLoad}
          />
          </div>
        ) : null}

        <Card id="report-details" className="report-part">
          <h1 className="report-title">Microsoft 365 security assessment</h1>

          <dl className="settings-grid">
            <dt>Organisation</dt>
            <dd>{w.name}</dd>

            {w.connection ? (
              <>
                <dt>Microsoft tenant</dt>
                <dd className="mono">{w.connection.tenantId}</dd>
              </>
            ) : null}

            <dt>Scope</dt>
            <dd>
              {area === null ? (
                'Every area assessed'
              ) : (
                <>
                  <strong>{area} only</strong>
                  {/* Loud, and in the document rather than only in the control that set it. A
                      filtered report handed on as "the report" is a document that omits whole
                      areas of a tenant without ever saying so. */}
                  <p className="report-warning">
                    This is a partial report. It covers {area} and leaves out every other area
                    of this tenant.
                  </p>
                </>
              )}
            </dd>

            <dt>Assessed</dt>
            <dd>
              {freshness.asOf ? formatDate(freshness.asOf) : 'Never'}
              {/* FOUR cases, read from the SERVER's verdict (complete / inconsistent), never
                  re-derived from `status` here -- api.ts forbids that, and this is the document an
                  auditor keeps. The two-way version printed "Complete: all 0 areas were assessed"
                  for a tenant never assessed, and "Complete: all 5 areas" for a scan recorded as
                  complete that covered three. */}
              {freshness.status === null ? (
                <p className="report-warning">
                  No assessment has completed for this tenant
                  {freshness.scanPending ? ' — the first one is queued' : ''}. Nothing in this report
                  describes the tenant yet.
                </p>
              ) : freshness.inconsistent ? (
                <p className="report-warning">
                  This assessment is recorded as finished but covered {freshness.areasCompleted} of{' '}
                  {freshness.areasTotal} areas. That is a fault on our side, not a result about your
                  tenant. <strong>Do not read this report as a complete assessment.</strong>
                </p>
              ) : freshness.status === 'complete' && freshness.complete ? (
                <p className="settings-note">
                  {/* NOT "all 4 areas were assessed" over an Email area nobody could read (0158,
                      owner 2026-09-26): an area complete only because nothing in it is licensed is
                      taken out of the assessed count and named, in the same words as the CSV. */}
                  {notLicensedCount === 0
                    ? `Complete: all ${freshness.areasTotal} areas were assessed.`
                    : `Complete: ${Math.max(freshness.areasTotal - notLicensedCount, 0)} of ${freshness.areasTotal} areas were assessed.`}
                  {partlyReadLine ? ` ${partlyReadLine}` : ''}
                  {unconfirmedLine ? ` ${unconfirmedLine}` : ''}
                  {notLicensedLine ? ` ${notLicensedLine}` : ''}
                </p>
              ) : (
                <p className="report-warning">
                  This assessment is <strong>{freshness.status}</strong>. It covered{' '}
                  {Math.max(freshness.areasCompleted - notLicensedCount, 0)} of {freshness.areasTotal}{' '}
                  areas, so the findings below are a floor and not a total.
                  {freshness.failureReason ? ` ${freshness.failureReason}` : ''}
                  {/* The reason is our own fragment ("unreachable: Identity") and usually has no
                      full stop; one is added only when a sentence follows it, as the CSV does. */}
                  {freshness.failureReason && notLicensedLine && !/[.!?]$/.test(freshness.failureReason.trim())
                    ? '.'
                    : ''}
                  {notLicensedLine ? ` ${notLicensedLine}` : ''}
                </p>
              )}
            </dd>

            {partlyRead.length > 0 ? <>
              <dt>Evidence coverage details</dt>
              <dd>
                {partlyRead.some((area) => area.checks.some((check) => check.gaps?.length))
                  ? <p className="settings-note">Counts are per reason and may overlap; they are not a total of affected identities or devices.</p>
                  : null}
                {partlyRead.map((area) => <div key={area.area}>
                  <strong>{area.area}</strong>
                  {area.checks.length > 0 ? <ul className="freshness-gap-reasons">
                    {area.checks.map((check) => <li key={check.key}>
                      {checkName(check)}
                      <ul>{gapDescriptions(check).map((reason, i) => <li key={i}>{reason}</li>)}</ul>
                    </li>)}
                  </ul> : <p className="settings-note">The affected checks could not be named from this record.</p>}
                </div>)}
              </dd>
            </> : null}

            <dt>Baseline &amp; version</dt>
            <dd>
              CISA SCuBA + additional checks
              <p className="settings-note">
                SCuBA-mapped findings — today Entra ID and Exchange Online, with Defender for
                Office 365 — cite their CISA policy id (for example MS.AAD.3.2v2). Other findings,
                including endpoint, data and some identity checks, are additional security checks
                kept outside the SCuBA count.
              </p>

              {versionData && versionData.recorded ? (
                <>
                  {versionData.catalogDrift === 'changed' ? (
                    <p className="report-warning">
                      The check set has changed since this assessment ran. Run a new assessment for
                      the current wording and mappings.
                    </p>
                  ) : null}

                  {versionData.changedDuringScan ? (
                    <p className="report-warning">
                      The check set or a baseline changed while this assessment ran, so the result is
                      not cleanly attributable to a single version.
                    </p>
                  ) : null}

                  {/* The identifiers an auditor checks, folded on screen and printed in full. A
                      baseline line that carries a warning keeps the whole list in view. */}
                  <Foldable
                    label="Versions and sealed record"
                    folded={versionData.baselines.every((b) => b.drift !== 'changed' && b.reconciledAtStart)}
                  >
                  <p className="settings-note">
                    {/* Identifiers kept -- an auditor may check them -- but in words a reader knows.
                        "Unreconciled", "registered release" and "pin was stale" were our pipeline's
                        vocabulary, not the reader's. */}
                    Assessed against check set{' '}
                    <span className="mono">{versionData.catalogFingerprintShort ?? 'unknown'}</span>
                    {versionData.catalogReconciled ? '' : ' (this check set does not match a published Defenssive release)'}
                    {versionData.scannerRulesetVersion
                      ? <> · assessment version <span className="mono">{versionData.scannerRulesetVersion}</span></>
                      : null}
                    .
                  </p>

                  {versionData.baselines.length > 0 ? (
                    <ul className="settings-note report-baselines">
                      {versionData.baselines.map((b) => (
                        <li key={b.product}>
                          {b.product}: {b.baselineName}, {b.source}, source revision{' '}
                          <span className="mono">{b.sourceRef.slice(0, 12)}</span>,{' '}
                          {b.pinnedPolicyCount} policies
                          {/* "Unreleased", never "published": this tracks CISA's working copy. */}
                          {b.exceedsRelease ? ` (tracks CISA's unreleased baseline, ahead of release ${b.exceedsRelease})` : ''}
                          {b.drift === 'changed' ? ' — changed since this assessment' : ''}
                          {!b.reconciledAtStart
                            ? ' — the baseline copy used was already out of date when this assessment began; run a new assessment for current results'
                            : ''}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {versionData.sealed ? (
                    <p className="settings-note">
                      A sealed record of an earlier assessment (recorded{' '}
                      {formatDate(versionData.sealed.sealedAt)}) is available to download — the report
                      exactly as that scan sealed it. It may be an earlier scan than the one shown above.
                      Its integrity reference travels in the file and is verified as you download it; a
                      record that cannot be verified is refused, not served.
                    </p>
                  ) : null}
                  </Foldable>
                </>
              ) : versionData && !versionData.recorded ? (
                <p className="settings-note">
                  This assessment predates version stamping, so the exact catalogue and baseline it
                  ran under are not recorded.
                </p>
              ) : versionUnavailable ? (
                <p className="report-warning">
                  The versions this report was assessed under could not be loaded, so they are not shown
                  here. Do not treat this report as version-stamped until it loads — reopen the page to
                  retry.
                </p>
              ) : null}
            </dd>

            {/* What Defenssive does, as Settings says it, not a capability ("Access held:
                Read-only ... Defenssive cannot alter a setting"): the Terms and the privacy notice
                say its two Exchange permissions are not read-only by name. */}
            <dt>Access</dt>
            <dd>
              Reads only.
              <p className="settings-note">
                Defenssive only reads: it makes no change in the assessed tenant, including to the
                settings reported here.
              </p>
            </dd>
          </dl>
        </Card>

        <Card id="report-summary" className="report-part" title="Summary" style={{ marginTop: 'var(--space-4)' }}>
          <p className="settings-lede">
            {freshness.status === null
              ? 'No assessment has run yet, so there is nothing to report.'
              : scopeTotal === 0
              ? 'No findings are open in this scope. That is what these checks found, not a ' +
                'statement that nothing is there.'
              : `${scopeTotal} finding${scopeTotal === 1 ? '' : 's'} open` +
                (area === null ? ' across the tenant.' : ` in ${area}.`)}
          </p>

          {scopeTotal > 0 && !breakdownReady ? (
            findings.state === 'error' ? (
              <p className="report-warning">
                The severity breakdown for {area} could not be loaded, so it is not shown. The total
                of {scopeTotal} above is exact.
              </p>
            ) : (
              <p className="settings-note">Loading the severity breakdown for {area}…</p>
            )
          ) : null}

          <div className="report-summary-tables">
          {scopeTotal > 0 && breakdownReady ? (
            <div>
              <SeverityBar counts={counts} total={scopeTotal} />

              <table className="settings-table report-severity">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {SEVERITIES.map((level) => (
                    <tr key={level}>
                      <td>
                        <span className={`report-swatch sev-${level.toLowerCase()}`} aria-hidden="true" />
                        {level}
                      </td>
                      <td className="mono">{counts[level] ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!countsAreExact ? (
                <p className="report-warning">
                  This area has more findings than one page returns, so the breakdown above
                  counts the {rows.length} listed below rather than all {scopeTotal}.
                </p>
              ) : null}
            </div>
          ) : null}

          {area === null && areas.length > 0 ? (
            <div>
              <h3 className="report-subheading">By area</h3>
              <table className="settings-table">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map(([name, count]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      <td className="mono">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          </div>
        </Card>

        <Card
          id="report-findings"
          className="report-part"
          title={area === null ? 'Findings' : `Findings — ${area}`}
          style={{ marginTop: 'var(--space-4)' }}
        >
          {findings.state === 'loading' ? <p className="live-empty">Loading…</p> : null}

          {findings.state === 'error' ? (
            <p className="live-error">{describeFailure(findings.status)}</p>
          ) : null}

          {/* Two wordings, because the counts above mean different things in the two scopes: for the
              whole tenant they are the server's own tally and complete; for one area the breakdown
              is derived from these rows, so only the area TOTAL is exact. One sentence claimed both. */}
          {capped ? (
            <p className="report-warning">
              {area === null || serverSplit !== null
                ? <>This scope has more open findings than one report page returns, so the {MAX_ROWS}{' '}
                    most severe are listed. The counts above are complete; this list is not.</>
                : <>This area has more open findings than one report page returns, so the {MAX_ROWS}{' '}
                    most severe are listed. Only the total of {scopeTotal} above is exact; the severity
                    breakdown and this list cover the {rows.length} shown.</>}
            </p>
          ) : null}

          {findings.state === 'ready' && rows.length === 0 ? (
            <p className="live-empty">Nothing is open in this scope.</p>
          ) : null}

          {rows.length > 0 ? (
            <TableScroll label="Findings in this report">
            <table className="settings-table report-findings">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Finding</th>
                  <th>Severity</th>
                  <th>Area</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f: FindingSummary) => (
                  <tr key={f.id}>
                    <td className="mono">{referenceOf(f.id)}</td>
                    <td>{f.title}</td>
                    <td>
                      <span className={`report-sev sev-${f.severity.toLowerCase()}`}>
                        {f.severity}
                      </span>
                    </td>
                    <td>{f.area}</td>
                    <td>{formatDate(f.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          ) : null}
        </Card>
      </div>
      </div>
    </>
  )
}

/**
 * The report's parts, to jump between on screen. Links, since each is a place in the page; the jump
 * is done here rather than by the address, so the page's address does not gain a #fragment. Hidden
 * on paper and on a narrow screen, where the report is read straight down.
 */
function ReportOutline({ parts }: { parts: { id: string; label: string; note?: string }[] }) {
  return (
    <nav className="report-outline" aria-label="In this report">
      <p className="report-outline-label">In this report</p>
      <ul>
        {parts.map((part) => (
          <li key={part.id}>
            <a
              href={`#${part.id}`}
              onClick={(event) => {
                event.preventDefault()
                document.getElementById(part.id)?.scrollIntoView({ block: 'start' })
              }}
            >
              {part.label}
              {part.note ? <span className="report-outline-note"> {part.note}</span> : null}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/**
 * Detail an auditor checks and a reader skims past: folded on screen behind a link-styled toggle,
 * always printed in full (a closed <details> would not print its contents). Not folded at all when
 * it carries a warning.
 */
function Foldable({ label, folded, children }: { label: string; folded: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  if (!folded) return <>{children}</>
  return (
    <>
      <button
        type="button"
        className="report-fold"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>
      <div id={id} className={open ? 'report-folded is-open' : 'report-folded'}>
        {children}
      </div>
    </>
  )
}

/**
 * The scope's findings as one bar, worst on the left.
 *
 * A shape rather than a chart: the question it answers is "how much of this is serious", which a
 * reader gets from proportion faster than from four numbers. The numbers are directly beneath it
 * either way — this replaces nothing, and a segment too thin to see still has its row in the
 * table.
 */
function SeverityBar({ counts, total }: { counts: Record<string, number>; total: number }) {
  const present = SEVERITIES.filter((level) => (counts[level] ?? 0) > 0)

  return (
    <div
      className="report-bar"
      role="img"
      aria-label={present
        .map((level) => `${counts[level]} ${level}`)
        .join(', ')}
    >
      {present.map((level) => (
        <span
          key={level}
          className={`report-bar-part sev-${level.toLowerCase()}`}
          style={{ width: `${((counts[level] ?? 0) / total) * 100}%` }}
        />
      ))}
    </div>
  )
}
